import { inject, Injectable } from '@angular/core';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { HostPrefillService } from '@workflow/ecp-workflow/dynamic-app/service/host-prefill.service';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { OaoPageHelper } from '../../helper/oao-page.helper';
import { CUDLConnect } from '../../../dynamic-app/component/building-block/app-prefill/app-prefill.type';
import { StatefulService } from '@app/service/stateful.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { ApplicationFillType } from '@workflow/ecp-shared/enum/enum';
import { FeatureFlagService } from '@cudirect/shared-ui-library';

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OaoCustomFunctionsService {
  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly flowHelper: OaoPageHelper,
    private readonly l360Service: L360Service,
    protected readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly prefillService: HostPrefillService,
    private readonly workflowLoader: WorkflowLoaderService,
    private readonly featureFlagService: FeatureFlagService
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public oaoFlowInitializer = (context: LogicFnContext): void => {
    const membershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value = membershipId;
    this.flowHelper.resetRequestedDoc();
    this.flowHelper.ValidateAllOAOPage();
    this.flowHelper.oaoNavigationInProgressApp();
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public identityContinuetext = (context: LogicFnContext): string => {
    const isEmptyPIDReport = this.statefulService.wrap('ss:oaoMemberInformation/isEmptyPIDReport').value;
    return isEmptyPIDReport ? 'Continue' : 'Verify my identity';
  };

  /** Loads static Accounst and Service response and caching it
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public LoadStaticAccounts = (context: LogicFnContext): void => {
    // before making api call for runautomated decision, lets clear the cache. Update it again, Once we get response.
    this.l360Service.cachedAccountAndService = null;
    this.l360Service.GetEligibileProductsFromL360().subscribe((data) => {
      if (data.isSuccess) this.l360Service.cachedAccountAndService = data.data;
      this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/isAccountsServiceCompleted').value = true;
    });
  };

  /**
   * Load Homebanking Prefill Data for OAO
   */
  public tryOAOLoadPrefillData = (context: LogicFnContext): Promise<boolean> => {
    return new Promise((resolve) => {
      this.prefillService.flowId = 'oao';
      const prefillOption = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/autoFillOption').valueString;
       const prefillDataWrap = this.statefulService.wrap('mem:home-banking-prefill-data');
      const prefillData = prefillDataWrap.valueString;

      // Check if Homebanking Prefill Server Side feature flag is enabled, then prefill will be handled server side
      const isHbPrefillServerSide = this.featureFlagService.isFeatureEnabled('enableOAOHBPrefillServerSide');
      if (isHbPrefillServerSide) {
        resolve(true);
        return;
      }

      // If no prefill option is selected, or if DXPrefill is selected, skip prefill
      if (prefillOption === ApplicationFillType.DXPrefill) {
        resolve(true);
        return;
      }

      // If no prefill data is available, skip prefill
      if (!prefillData) {
        this.workflowLoader.end();
        resolve(true);
        return;
      }

      prefillDataWrap.value = null;
      this.statefulService.wrap('api:hostPrefillToken').value = null;

      const parsedData = JSON.parse(prefillData) as CUDLConnect;
      if (!parsedData) {
        this.workflowLoader.end();
        resolve(true);
        return;
      }

      this.statefulService.wrap('mem:oao-prefill-inprogress').value = true;
      let pageIterationId: number;
      const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
      let apiPrimaryRoot = 'primaryApplicant';
      if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
        apiPrimaryRoot = 'businessInformation/responsibleParties/#';
        pageIterationId = 0;
      }
      if (this.flowHelper.flow.config.streamlined) {
        this.workflowLoader.start('Connecting to your profile...', {
          animationId: 'documentscan',
          overlayMode: 'card',
          segments: this.flowHelper.flow.config.streamlinedSkipPages.length
        });
      }
      this.prefillService.checkAndAddApplicant(`api:oaoMemberInformation/${apiPrimaryRoot}`).then(() => {
        this.prefillService.updatePrefillDataForOao(
          parsedData,
          () => {
            this.statefulService.wrap('api:hostPrefillToken').value = null;
            this.statefulService.wrap('mem:oao-prefill-inprogress').value = false;
            const checkForCondensedView = () => {
              const [previewCardId] = context.page.cards.filter((t) => t.config.customCardType === 'autofill-preview').map((t) => t.id);

            // This checks if any card on a page has tcpa checkbox that is visible. 
            // If it does, it will not show the condensed view
            const isTcpaFieldVisible = context.page?.cards?.some(card => 
              card.fields?.some(field => 
                field.id?.includes('tcpa') && field.visible === true && field.data.value !== true
              )
            ) || false;

              if (
                this.flowHelper.flow.config.streamlined ||
                context.page.cards.filter((t) => t.visible && t.helper.flatVisibleFields.findIndex((f) => f.required && !f.valid) !== -1)
                  .length !== 0
              ) {
                this.statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = true;
              } else if(isTcpaFieldVisible) {
                this.statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = true;
              } else {
                this.statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = false;
              }
            };
            setTimeout(checkForCondensedView, 0);
            resolve(true);
          },
          apiPrimaryRoot,
          pageIterationId
        );
      });
    });
  };

  /**
   * Prefill OAO Business Info Page details
   */
  public prefillBusinessInfoData = () => {
    const isloading = this.statefulService.wrap('mem:isloading').value as boolean;
    const otuToken = this.statefulService.wrap('api:hostPrefillToken').value as string;
    if (isloading !== true && otuToken) {
      this.statefulService.wrap('mem:isloading').value = true;
      this.prefillService.prefillBusinessInfo(otuToken);
    }
  };

  /** load custom field to memory cache */
  public getOAOCustomFields = (context: LogicFnContext, membershipID: string) => {
    this.l360Service.getOAODynamicCustomFields({ membershipID }).subscribe({
      next: (res) => {
        if (res.isSuccess && res.data) this.l360Service.cachedCustomfields = res.data.applicationCustomFields;
      }
    });
  };
}
